import { MouseEvent, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Dot, RadioField, useFormField } from '@frontend/design-system';
import { LocationServiceLocationItem } from '../../types/location.types';

export interface LocationItemProps {
  altContent?: React.ReactNode;
  location: LocationServiceLocationItem;
  hasNotice?: boolean;
  isSelected: (locationId: string) => boolean;
  isCurrent: (locationId: string) => boolean;
  onSelect: (location: LocationServiceLocationItem) => void;
}

const styles = {
  root: css`
    display: flex;
    min-height: 40px;
    gap: ${theme.spacing(2)};
    align-items: center;
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
    cursor: pointer;

    &:hover {
      background-color: ${theme.colors.neutral10};
    }
  `,
  altContent: css`
    color: ${theme.colors.neutral50};
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.regular};
    margin-left: auto;
  `,
  childrenLocationToggle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    cursor: pointer;
    white-space: nowrap;

    .icon {
      min-width: 8px;
    }
  `,
  radioInput: css`
    height: 20px;

    & label {
      display: none;
    }
  `,
  locationName: css`
    font-weight: ${theme.font.weight.bold};
    display: flex;
    gap: ${theme.spacing(1)};
    position: relative;
  `,
  nestedLists: css`
    padding: 0 ${theme.spacing(2)};
  `,
};

export const LocationItem = ({
  altContent,
  location,
  hasNotice,
  onSelect,
  isSelected,
  isCurrent,
}: LocationItemProps) => {
  const handleClick = () => onSelect(location);
  const selected = isSelected(location.locationId);
  const current = isCurrent(location.locationId);

  const selectField = useFormField(
    {
      type: 'radio',
      value: selected ? location.locationId : 'other',
    },
    [location.locationId, isSelected]
  );

  return (
    <div className='select-item' css={styles.root} onClick={handleClick}>
      <RadioField css={styles.radioInput} {...selectField} name='selected_location'>
        {[0].map((val) => (
          <RadioField.Radio key={val} value={location.locationId}>
            {null}
          </RadioField.Radio>
        ))}
      </RadioField>
      <div css={styles.locationName}>
        <span>{location.name}</span>
        {current && <Chip color='outline'>current</Chip>}
        {hasNotice && (
          <span>
            <Dot color='warning' />
          </span>
        )}
      </div>
      <div css={styles.altContent}>{altContent}</div>
    </div>
  );
};

export interface ParentLocationItemProps extends Omit<LocationItemProps, 'altContent'> {
  location: LocationServiceLocationItem & { children: LocationServiceLocationItem[] };
}

export const ParentLocationItem = ({ location, onSelect, isSelected, isCurrent }: ParentLocationItemProps) => {
  const isChildSelected = () => location.children.some((child) => isSelected(child.locationId));
  const isChildCurrent = () => location.children.some((child) => isCurrent(child.locationId));

  const [isOpen, setIsOpen] = useState(isChildSelected() || isChildCurrent());

  const handleToggleChildren = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setIsOpen(!isOpen);
  };

  const hasChildSelected = useMemo(() => isChildSelected() || isChildCurrent(), [location, isSelected, isCurrent]);

  return (
    <>
      <LocationItem
        location={location}
        onSelect={onSelect}
        isSelected={isSelected}
        isCurrent={isCurrent}
        hasNotice={hasChildSelected}
        altContent={
          !!location.children.length && (
            <div css={styles.childrenLocationToggle} onClick={handleToggleChildren}>
              <div>
                {location.children.length} {location.children.length === 1 ? 'Location' : 'Locations'}
              </div>
              <div>
                <Icon className='icon' size={8} name={isOpen ? 'alt-caret-up-tiny' : 'alt-caret-down-tiny'} />
              </div>
            </div>
          )
        }
      />
      {isOpen && (
        <div css={styles.nestedLists}>
          {location.children.map((child) => (
            <LocationItem
              key={child.locationId}
              location={child}
              isSelected={isSelected}
              isCurrent={isCurrent}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </>
  );
};
